import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useCategoryApi from "hooks/api/private/useCategoryApi";

import { CategoryBody, CategoryDetails, CategoryTextItem } from "types/api/category";
import { LanguageEnum } from "types/api/commons";
import { IndoorOrOutdoor } from "types/enums";

import { emptyMetaFields, metaFieldsSchema } from "utils/metaFields";

export type CategoryBodyForm = {
    type: IndoorOrOutdoor;
    graphical_order: number | null;
    text: {
        [x in LanguageEnum]: CategoryTextItem;
    };
};

export type CategoryFormHook = {
    formHook: FormHookType<CategoryBodyForm>;
};

function useCategoryForm({ categoryId }: { categoryId?: string }): CategoryFormHook {
    const { createCategory, updateCategory, getOneCategory } = useCategoryApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                type: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                graphical_order: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            name: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            slug: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            ...metaFieldsSchema.of.properties,
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: CategoryBodyForm = React.useMemo(() => {
        return {
            type: IndoorOrOutdoor.indoor,
            graphical_order: null,
            text: {
                it: {
                    name: "",
                    slug: "",
                    ...emptyMetaFields()[LanguageEnum.it],
                },
                en: {
                    name: "",
                    slug: "",
                    ...emptyMetaFields()[LanguageEnum.en],
                },
            },
        };
    }, []);

    const formHook = useForm<CategoryBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [categoryDetails, setCategoryDetails] = React.useState<CategoryDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (categoryId) {
            const [apiCall, abortController] = getOneCategory({ id: categoryId });

            apiCall.then(setCategoryDetails);

            return () => abortController.abort();
        }
    }, [getOneCategory, categoryId]);

    React.useEffect(() => {
        if (categoryDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.type = categoryDetails.type;
                newForm.graphical_order = categoryDetails.graphical_order;
                newForm.text.it.name = categoryDetails.text.it.name;
                newForm.text.en.name = categoryDetails.text.en.name;
                newForm.text.it.slug = categoryDetails.text.it.slug;
                newForm.text.en.slug = categoryDetails.text.en.slug;
                newForm.text.it.meta_title = categoryDetails.text.it.meta_title ?? "";
                newForm.text.it.meta_description = categoryDetails.text.it.meta_description ?? "";
                newForm.text.it.meta_keywords = categoryDetails.text.it.meta_keywords ?? "";
                newForm.text.en.meta_title = categoryDetails.text.en.meta_title ?? "";
                newForm.text.en.meta_description = categoryDetails.text.en.meta_description ?? "";
                newForm.text.en.meta_keywords = categoryDetails.text.en.meta_keywords ?? "";

                return newForm;
            });
        }
    }, [setForm, categoryDetails]);

    const categoryBody: CategoryBody = React.useMemo(() => {
        const body: CategoryBody = {
            type: form.type,
            graphical_order: form.graphical_order ?? 0,
            text: {
                it: { ...form.text.it },
                en: { ...form.text.en },
            },
        };

        return body;
    }, [form]);

    React.useEffect(() => {
        addSaveAction("useCategoryForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                if (categoryId) {
                    const [apiCall] = updateCategory({ id: categoryId }, categoryBody);

                    promises.push(apiCall);
                } else {
                    const [apiCall] = createCategory(categoryBody);

                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createCategory,
        updateCategory,
        categoryBody,
        categoryId,
        isValid,
    ]);

    return React.useMemo(
        (): CategoryFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default useCategoryForm;
